/**
 * Pure tile hit-testing. Given a tap/hover point in canvas/CSS pixels (or, for
 * pond content, WORLD pixels — see `hitTestDiscardPile`/`hitTestMeldStrip`),
 * returns the index of the tile under the point (or null). No canvas, no
 * engine — the geometry mirrors the matching renderer by reusing the exact
 * same placement functions (`spreadRow` / `gridSlots` / `meldSlots`), so a
 * layout change can never desync the draw and the hit-test.
 */

import type { Rect, SeatZone } from '../layout/types.js';
import {
  DRAWN_TILE_LIFT_PX,
  HAND_TILE_GAP_PX,
  MIN_TILE_STRIDE_PX,
  TILE_HEIGHT,
  TILE_WIDTH,
} from '../utils/constants.js';
import {
  gridSlots,
  meldSlots,
  spreadRow,
  type GridAxis,
  type GridWrapFrom,
  type Slot,
} from '../render/tileLayout.js';

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Index of the local-hand tile under `point`, or null. The rack is drawn along
 * the horizontal bottom zone left→right, so later tiles overlay earlier ones —
 * we scan from the last (top-most) tile back so an overlap resolves to the tile
 * actually visible there. The vertical band is extended upward by the drawn-tile
 * lift so the raised freshly-drawn tile stays tappable.
 */
export function hitTestHandTile(point: Point, zone: SeatZone, tileCount: number): number | null {
  if (tileCount <= 0) return null;
  const spread = spreadRow(tileCount, TILE_WIDTH, HAND_TILE_GAP_PX, MIN_TILE_STRIDE_PX, zone.width);
  const cy = zone.y + zone.height / 2;
  const top = cy - TILE_HEIGHT / 2 - DRAWN_TILE_LIFT_PX;
  const bottom = cy + TILE_HEIGHT / 2;
  if (point.y < top || point.y > bottom) return null;
  for (let i = spread.visibleCount - 1; i >= 0; i -= 1) {
    const cx = zone.x + spread.startCenter + i * spread.stride;
    if (point.x >= cx - TILE_WIDTH / 2 && point.x <= cx + TILE_WIDTH / 2) return i;
  }
  return null;
}

/** Index of the slot (scanned back-to-front, matching draw overlap order) whose
 *  `boxW × boxH` bounding box centred on its CENTRE contains `point`, or null. */
function hitTestSlots(point: Point, slots: readonly Slot[], boxW: number, boxH: number): number | null {
  for (let i = slots.length - 1; i >= 0; i -= 1) {
    const slot = slots[i];
    if (slot === undefined) continue;
    if (
      point.x >= slot.cx - boxW / 2 &&
      point.x <= slot.cx + boxW / 2 &&
      point.y >= slot.cy - boxH / 2 &&
      point.y <= slot.cy + boxH / 2
    ) {
      return i;
    }
  }
  return null;
}

/**
 * Index of the discard-pile tile under `point`, or null. `point` must already
 * be in the SAME space `cell` is — world space when called against a panned/
 * zoomed pond (see `scenes/SceneCallbacks.findHoveredTile`). Mirrors
 * `DiscardRenderer.drawDiscardPond`'s exact fpX/fpY swap + axis choice for
 * sideways (left/right) seats.
 */
export function hitTestDiscardPile(
  point: Point,
  cell: Rect,
  count: number,
  gap: number,
  sideways: boolean,
  wrapFrom: GridWrapFrom,
): number | null {
  const fpX = sideways ? TILE_HEIGHT : TILE_WIDTH;
  const fpY = sideways ? TILE_WIDTH : TILE_HEIGHT;
  const axis: GridAxis = sideways ? 'vertical' : 'horizontal';
  const slots = gridSlots(cell, count, fpX, fpY, gap, axis, wrapFrom);
  return hitTestSlots(point, slots, fpX, fpY);
}

/**
 * Index of the meld tile under `point`, or null. `point` must be in the same
 * (world) space as `zone`. Mirrors `HandRenderer.drawMelds`'s tile sizing —
 * meld tiles are always drawn at `TILE_WIDTH/HEIGHT * MELD_TILE_SCALE`
 * regardless of rotation (see `TileRect`'s upright-frame convention), so the
 * hit box swaps width/height for sideways seats the same way discards do.
 */
export function hitTestMeldStrip(
  point: Point,
  zone: Rect,
  count: number,
  tileW: number,
  tileH: number,
  gap: number,
  endInset: number,
  sideways: boolean,
): number | null {
  const horizontal = !sideways;
  const slots = meldSlots(zone, count, tileW, gap, endInset, horizontal);
  const boxW = sideways ? tileH : tileW;
  const boxH = sideways ? tileW : tileH;
  return hitTestSlots(point, slots, boxW, boxH);
}
